import React, { useState } from "react";
import {
  Modal,
  //  Grid, // REMOVIDO: Eliminando o componente Grid
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { API_URLS } from "../../config/apiUrls";

interface CriarSorteioModalProps {
  open: boolean;
  onClose: () => void;
  token: string;
  onSorteioCreated: () => void;
}

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "90%", sm: 600, md: 700 },
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  p: 4,
  maxHeight: "90vh",
  overflowY: "auto",
};

const CriarSorteioModal: React.FC<CriarSorteioModalProps> = ({
  open,
  onClose,
  token,
  onSorteioCreated,
}) => {
  const [title, setTitle] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [startDate, setStartDate] = useState<string>("");
  const [drawDate, setDrawDate] = useState<string>("");
  const [displayEndDate, setDisplayEndDate] = useState<string>("");
  const [minNumber, setMinNumber] = useState<number | "">(1);
  const [maxNumber, setMaxNumber] = useState<number | "">(9999);
  const [ticketsPerUser, setTicketsPerUser] = useState<number | "">(3);
  const [participationRule, setParticipationRule] = useState<string>("");

  const [prizeType, setPrizeType] = useState<"fixed" | "dynamic">("fixed");
  const [totalPrize, setTotalPrize] = useState<number | "">(0);
  const [prizePercentage, setPrizePercentage] = useState<number | "">("");

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setTitle("");
      setDescription("");
      setStartDate("");
      setDrawDate("");
      setDisplayEndDate("");
      setMinNumber(1);
      setMaxNumber(9999);
      setTicketsPerUser(3);
      setParticipationRule("");
      setPrizeType("fixed");
      setTotalPrize(0);
      setPrizePercentage("");
      setError(null);
      setSuccess(null);
    }
  }, [open]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    // Validação básica de campos obrigatórios
    if (
      !title ||
      !description ||
      !startDate ||
      !drawDate ||
      !displayEndDate ||
      minNumber === "" ||
      maxNumber === "" ||
      ticketsPerUser === "" ||
      !participationRule
    ) {
      setError("Por favor, preencha todos os campos obrigatórios.");
      setLoading(false);
      return;
    }

    if (prizeType !== "fixed" && prizePercentage === "") {
      setError(
        "A porcentagem do prêmio é obrigatória para o prêmio dinâmico ou apenas porcentagem.",
      );
      setLoading(false);
      return;
    }

    // Validação de valores numéricos
    if (
      Number(minNumber) < 1 ||
      Number(maxNumber) < Number(minNumber) ||
      Number(ticketsPerUser) < 1
    ) {
      setError(
        "Verifique os números de bilhetes e ranges. minNumber deve ser >= 1, maxNumber >= minNumber, e ticketsPerUser >= 1.",
      );
      setLoading(false);
      return;
    }

    if (
      prizeType !== "fixed" &&
      typeof prizePercentage === "number" &&
      (prizePercentage < 0.01 || prizePercentage > 100)
    ) {
      setError("A porcentagem de aumento deve estar entre 0.01% e 100%.");
      setLoading(false);
      return;
    }

    // Construção do payload
    const payload: { [key: string]: any } = {
      title,
      description,
      startDate: new Date(startDate).toISOString(),
      drawDate: new Date(drawDate).toISOString(),
      displayEndDate: new Date(displayEndDate).toISOString(),
      minNumber: Number(minNumber),
      maxNumber: Number(maxNumber),
      ticketsPerUser: Number(ticketsPerUser),
      participationRule,
    };

    // Adicionando os campos de prêmio com base no tipo
    if (prizeType === "fixed") {
      payload.totalPrize = Number(totalPrize);
      payload.prizePercentage = null;
    } else if (prizeType === "dynamic") {
      payload.totalPrize = Number(totalPrize);
      payload.prizePercentage = Number(prizePercentage);
    }

    try {
      const response = await fetch(API_URLS.sorteios, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Erro ao criar sorteio.");
      }

      setSuccess("Sorteio criado com sucesso!");
      onSorteioCreated();
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Ocorreu um erro desconhecido ao criar o sorteio.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="create-sorteio-modal-title"
      aria-describedby="create-sorteio-modal-description"
    >
      <Box sx={style} component="form" onSubmit={handleSubmit}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography
            id="create-sorteio-modal-title"
            variant="h6"
            component="h2"
          >
            Criar Novo Sorteio
          </Typography>
          <IconButton onClick={onClose} aria-label="fechar">
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 2 }} />

        {/* Form Fields */}
        <TextField
          label="Título do Sorteio"
          fullWidth
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          margin="normal"
          required
        />
        <TextField
          label="Descrição"
          fullWidth
          multiline
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          margin="normal"
          required
          InputProps={{
            sx: {
              "& .MuiInputBase-inputMultiline": {
                resize: "vertical",
              },
            },
          }}
        />
        <TextField
          label="Regra de Participação"
          fullWidth
          multiline
          rows={2}
          value={participationRule}
          onChange={(e) => setParticipationRule(e.target.value)}
          margin="normal"
          required
          InputProps={{
            sx: {
              "& .MuiInputBase-inputMultiline": {
                resize: "vertical",
              },
            },
          }}
        />

        {/* Detalhes do Sorteio - SUBSTITUINDO GRID */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: "16px", // Simula spacing={2}
            mt: 1,
            // Ajuste para garantir que cada item ocupe metade da largura em telas sm e acima
            "& > div": {
              width: { xs: "100%", sm: "calc(50% - 8px)" }, // 50% - metade do gap
              margin: 0,
            },
          }}
        >
          {/* Data de Início das Inscrições */}
          <TextField
            label="Data de Início das Inscrições"
            type="datetime-local"
            fullWidth
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            margin="normal"
            InputLabelProps={{ shrink: true }}
            required
          />
          {/* Data do Sorteio */}
          <TextField
            label="Data do Sorteio"
            type="datetime-local"
            fullWidth
            value={drawDate}
            onChange={(e) => setDrawDate(e.target.value)}
            margin="normal"
            InputLabelProps={{ shrink: true }}
            required
          />
          {/* Data de Fim de Exibição */}
          <TextField
            label="Data de Fim de Exibição"
            type="datetime-local"
            fullWidth
            value={displayEndDate}
            onChange={(e) => setDisplayEndDate(e.target.value)}
            margin="normal"
            InputLabelProps={{ shrink: true }}
            required
          />
          {/* Bilhetes por Usuário */}
          <TextField
            label="Bilhetes por Usuário"
            type="number"
            fullWidth
            value={ticketsPerUser}
            onChange={(e) =>
              setTicketsPerUser(
                e.target.value === "" ? "" : Number(e.target.value),
              )
            }
            margin="normal"
            InputLabelProps={{ shrink: true }}
            required
            inputProps={{ min: 1 }}
          />
          {/* Número Mínimo de Bilhete */}
          <TextField
            label="Número Mínimo de Bilhete"
            type="number"
            fullWidth
            value={minNumber}
            onChange={(e) =>
              setMinNumber(e.target.value === "" ? "" : Number(e.target.value))
            }
            margin="normal"
            InputLabelProps={{ shrink: true }}
            required
            inputProps={{ min: 1 }}
          />
          {/* Número Máximo de Bilhete */}
          <TextField
            label="Número Máximo de Bilhete"
            type="number"
            fullWidth
            value={maxNumber}
            onChange={(e) =>
              setMaxNumber(e.target.value === "" ? "" : Number(e.target.value))
            }
            margin="normal"
            InputLabelProps={{ shrink: true }}
            required
            // A validação de inputProps={{ min: (minNumber as number) + 1 }} é complexa no JSX e é melhor mantida no `handleSubmit`.
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Configuração do Prêmio */}
        <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
          Configuração do Prêmio
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "16px" }}>
          {/* Tipo de Prêmio */}
          <Box sx={{ width: { xs: "100%", sm: "calc(50% - 8px)" } }}>
            <FormControl fullWidth margin="normal" required>
              <InputLabel>Tipo de Prêmio</InputLabel>
              <Select
                value={prizeType}
                label="Tipo de Prêmio"
                onChange={(e) =>
                  setPrizeType(e.target.value as "fixed" | "dynamic")
                }
              >
                <MenuItem value="fixed">Prêmio Fixo</MenuItem>
                <MenuItem value="dynamic">Prêmio Dinâmico</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Valor Base do Prêmio */}
          <Box sx={{ width: { xs: "100%", sm: "calc(50% - 8px)" } }}>
            <TextField
              label="Valor Base do Prêmio"
              type="number"
              fullWidth
              value={totalPrize}
              onChange={(e) =>
                setTotalPrize(
                  e.target.value === "" ? "" : Number(e.target.value),
                )
              }
              margin="normal"
              required
              inputProps={{ min: 0, step: "0.01" }}
            />
          </Box>

          {prizeType !== "fixed" && (
            <Box sx={{ width: { xs: "100%", sm: "calc(50% - 8px)" } }}>
              <TextField
                label="Porcentagem de Aumento (%)"
                type="number"
                fullWidth
                value={prizePercentage}
                onChange={(e) =>
                  setPrizePercentage(
                    e.target.value === "" ? "" : Number(e.target.value),
                  )
                }
                margin="normal"
                required
                inputProps={{ min: 0.01, max: 100, step: "0.01" }}
              />
            </Box>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {success}
          </Alert>
        )}

        <Box
          sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 2 }}
        >
          <Button
            variant="outlined"
            color="error"
            onClick={onClose}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : "Criar Sorteio"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default CriarSorteioModal;
